/* eslint-disable complexity */
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-nested-ternary */

'use client'

import React, { useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Chip,
  TextField,
  Divider,
  Stack,
  Skeleton,
  SxProps,
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { appPaths } from '@config'
import { PlanType } from '@query/page-query/pricing-query/pricing.model'
import { Image } from '@media-components'
import { ActionButton } from '@components/common'
import { portalUrl } from '@utils/environment'
import { publicImages } from '@utils/shared'
import { getStyles } from '@utils/styles'
import { useConsolidatedDynamicPricing } from 'src/client-query/pricing'
import { usePricingContext } from 'src/context'
import defaultStyle from './styles'

const exceededLimit = 500000

/**
 * Skeleton component for text loading states
 */
function TextSkeleton({
  width = 80,
  height = 20,
  sx,
}: {
  width?: number | string
  height?: number
  sx?: SxProps
}) {
  return (
    <Skeleton
      variant="text"
      width={width}
      height={height}
      sx={{ display: 'inline-block', ...sx }}
    />
  )
}

export function PricingCard() {
  const styles = getStyles(defaultStyle)
  const {
    setCurrentPlanType,
    setDynamicPricingUSDAnnual,
    setDynamicPricingUSDMonthly,
    setDynamicPricingINRAnnual,
    setDynamicPricingINRMonthly,
    getCurrentPricing,
  } = usePricingContext()
  const inputRef = useRef<HTMLInputElement>(null)

  const DEFAULT_MONTHLY_SPEND = 20000

  // Fetch all pricing (USD/INR × ANNUAL/MONTHLY) in a single API call
  const {
    pricingUSDAnnual,
    pricingUSDMonthly,
    pricingINRAnnual,
    pricingINRMonthly,
    annualSpend,
    setAnnualSpend,
  } = useConsolidatedDynamicPricing({
    initialSpend: DEFAULT_MONTHLY_SPEND,
    debounceMs: 500,
    amountLimit: exceededLimit,
  })

  // Update context when any pricing data changes
  useEffect(() => {
    if (pricingUSDAnnual) {
      setCurrentPlanType(pricingUSDAnnual.plan_type as PlanType)
      setDynamicPricingUSDAnnual(pricingUSDAnnual)
    }
    if (pricingUSDMonthly) {
      setDynamicPricingUSDMonthly(pricingUSDMonthly)
    }
    if (pricingINRAnnual) {
      setDynamicPricingINRAnnual(pricingINRAnnual)
    }
    if (pricingINRMonthly) {
      setDynamicPricingINRMonthly(pricingINRMonthly)
    }
  }, [
    pricingUSDAnnual,
    pricingUSDMonthly,
    pricingINRAnnual,
    pricingINRMonthly,
    setCurrentPlanType,
    setDynamicPricingUSDAnnual,
    setDynamicPricingUSDMonthly,
    setDynamicPricingINRAnnual,
    setDynamicPricingINRMonthly,
  ])

  // Get current pricing based on selected currency (default to Annual for display)
  const pricing = getCurrentPricing('ANNUAL')

  const handleSpendChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '')
    setAnnualSpend(value)
  }

  const convertedAnnualSpend = Number(annualSpend)

  const projectedSavings =
    convertedAnnualSpend > exceededLimit
      ? 'Contact for estimate'
      : `${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(pricing?.projected_net_savings ?? 0)}/yr approx`

  const monthlyPrice = pricing ? pricing.monthly_price_for_annual_plan : null

  const monthlyLabelPrice = pricing ? pricing.label_price_usd : null

  const helperText = () => {
    if (convertedAnnualSpend === 0) {
      return 'Monthly spend should be greater than (>)0'
    }
    if (convertedAnnualSpend > exceededLimit) {
      return 'Monthly spend reached. Kindly contact to our experts to get started.'
    }
    return undefined
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const t = useTranslations('Pricing.pricingCard')

  return (
    <Box {...styles('cardContainer')}>
      {/* Most Popular Badge */}
      <Box {...styles('popularBadge')}>Most Popular</Box>

      {/* Header */}
      <Stack>
        <Box {...styles('header')}>
          <Image
            src={publicImages.growthImage}
            alt="growth-image"
            {...styles('growthImageWrapper')}
            style={{ width: '24px', height: '24px', marginTop: '4px' }}
          />
          {/* plan_name */}
          {!pricing?.plan_name ? (
            <TextSkeleton width={120} sx={{ height: '100% !important' }} />
          ) : (
            <Typography variant="h6.600" sx={{ marginTop: '-0.5rem' }}>
              {pricing.plan_name} Plan
            </Typography>
          )}
          {pricing?.trial_period ? (
            <Chip
              icon={<Box {...styles('trialChipIcon')} />}
              label={t('chipLabel', {
                trailDays: pricing.trial_period,
              })}
              variant="trans"
              color="primary"
              {...styles('trialChip')}
            />
          ) : null}
        </Box>
        <Typography variant="body3.500" color="text.secondary">
          Stop paying for cloud waste. we claim up to 30% of your Cloud account
          spend with automated right-sizing. Most teams see result in under 48
          hours.
        </Typography>
      </Stack>

      <Divider />

      <Stack gap="12px">
        <TextField
          fullWidth
          value={annualSpend ? `$${annualSpend}` : '$'}
          onChange={handleSpendChange}
          {...styles('textField')}
          variant="outlined"
          label="Enter your Annual Spend"
          helperText={helperText()}
          error={convertedAnnualSpend === 0}
          inputRef={inputRef}
          type="text"
        />
        <Box {...styles('savingsBox')}>
          <Image
            src={publicImages.leaf}
            alt="leaf-image"
            {...styles('leafImageWrapper')}
            style={{
              width: '1rem',
              height: '1rem',
              marginTop: '4px',
            }}
          />
          <Typography variant="caption1.500">
            Projected Net Savings:{' '}
            <Typography variant="caption1.600">{projectedSavings}</Typography>
          </Typography>
        </Box>
      </Stack>

      {pricing?.plan_type !== 'ENTERPRISE' && (
        <Box>
          {!pricing?.discount ? (
            <Skeleton
              variant="rounded"
              width={80}
              height={24}
              {...styles('discountBadge')}
            />
          ) : (
            <Box {...styles('discountBadge')}>{`${pricing.discount}% OFF`}</Box>
          )}

          <Box {...styles('priceContainer')}>
            {/* price_usd */}
            {!monthlyPrice ? (
              <TextSkeleton width={100} height={40} />
            ) : (
              <Typography variant="h5.600">${monthlyPrice || '0'}</Typography>
            )}

            {/* label_price_usd */}
            {!monthlyLabelPrice ? (
              <TextSkeleton width={60} height={24} sx={{ alignSelf: 'end' }} />
            ) : (
              <Typography
                variant="body1.500"
                color="text.secondary"
                sx={{ textDecoration: 'line-through' }}
              >
                ${monthlyLabelPrice || '0'}
              </Typography>
            )}

            <Typography
              variant="caption1.500"
              color="text.secondary"
              sx={{ alignSelf: !monthlyLabelPrice ? 'end' : undefined }}
            >
              /mo for 12 months
            </Typography>
          </Box>
          <Typography
            variant="caption1.700"
            sx={{
              color: 'green.600',
            }}
          >
            (This plan pays for itself 8x over)
          </Typography>
        </Box>
      )}

      <Stack gap={1.5}>
        <ActionButton
          showArrow
          iconSx={{ color: 'common.white' }}
          link={
            pricing?.plan_type === 'GROWTH'
              ? `${portalUrl}register`
              : appPaths.contactUs
          }
          {...styles('ctaButton')}
        >
          {pricing?.plan_type === 'ENTERPRISE'
            ? t('button.enterpriseLabel')
            : pricing?.trial_period
              ? t('button.planLabel', {
                  trailDays: pricing.trial_period,
                })
              : t('button.planLabel0')}
        </ActionButton>

        <Box {...styles('guaranteeContainer')}>
          <Image
            src={publicImages.policyBadge}
            alt="policy-badge-image"
            {...styles('policyBadgeWrapper')}
            style={{
              width: '32px',
              height: '32px',
              marginTop: '4px',
            }}
          />
          <Typography variant="caption1.500">
            <Typography variant="caption1.600">
              The Zero-Risk Guarantee:
            </Typography>{' '}
            If we don't identify more savings than the cost of your
            subscription, your first month is on us.
          </Typography>
        </Box>
      </Stack>
    </Box>
  )
}
